import { Moeda, MOEDA_CONFIG } from '../enums/moeda.enum';

/**
 * Matriz canonica de pares de moedas permitidos para conversao.
 * Espelha MoedaPareamentoValidator.kt no backend.
 *
 * - GUARANI e universal (converte com todas).
 * - REAL e DOLAR convertem entre si e com GUARANI (nao com PESO).
 * - PESO converte apenas com GUARANI e com ele mesmo.
 */
export const MOEDAS_COMPATIVEIS: Record<Moeda, Moeda[]> = {
  [Moeda.GUARANI]: [Moeda.GUARANI, Moeda.REAL, Moeda.DOLAR, Moeda.PESO],
  [Moeda.REAL]:    [Moeda.GUARANI, Moeda.REAL, Moeda.DOLAR],
  [Moeda.DOLAR]:   [Moeda.GUARANI, Moeda.REAL, Moeda.DOLAR],
  [Moeda.PESO]:    [Moeda.GUARANI, Moeda.PESO],
};

/**
 * Verifica se ha conversao permitida entre origem e destino.
 * Retorna false para valores nulos/indefinidos ou moedas desconhecidas.
 * Aceita tanto o enum Moeda quanto strings cruas ("PESO", "GUARANI", etc.)
 * vindas do backend, por robustez.
 */
export function podeConverterEntre(
  origem: Moeda | string | null | undefined,
  destino: Moeda | string | null | undefined,
): boolean {
  if (!origem || !destino) return false;
  const from = origem as Moeda;
  const to = destino as Moeda;
  const destinos = MOEDAS_COMPATIVEIS[from];
  return destinos ? destinos.includes(to) : false;
}

/**
 * Lista moedas compativeis para filtrar dropdowns de destino a partir da origem.
 * Retorna array vazio quando a moeda nao e conhecida.
 */
export function moedasCompativeis(moeda: Moeda | string | null | undefined): Moeda[] {
  if (!moeda) return [];
  const key = moeda as Moeda;
  return MOEDAS_COMPATIVEIS[key] ? [...MOEDAS_COMPATIVEIS[key]] : [];
}

/**
 * Intersecao das listas de moedas compativeis para duas moedas-eixo.
 *
 * Usada quando ha DUAS restricoes de pareamento simultaneas — tipicamente:
 *   - moedaA = moeda do registro (ex.: gasto/receita em Guarani)
 *   - moedaB = moeda padrao da empresa (ex.: empresa configurada em Real)
 *
 * O resultado e o conjunto de moedas que satisfazem AMBAS as restricoes,
 * preservando a ordem da matriz de `moedaA` (estavel para UI).
 *
 * Comportamento defensivo:
 *  - Se qualquer um dos eixos for nulo/indefinido/desconhecido, retorna a
 *    matriz do eixo restante (degenera para `moedasCompativeis(eixoRestante)`).
 *  - Se ambos forem invalidos, retorna `[]`.
 *
 * Exemplos (registro x empresa):
 *  - GUARANI x REAL    -> [GUARANI, REAL, DOLAR]   (3 cards, sem PESO)
 *  - GUARANI x PESO    -> [GUARANI, PESO]          (2 cards)
 *  - REAL    x REAL    -> [GUARANI, REAL, DOLAR]   (3 cards)
 *  - PESO    x REAL    -> [GUARANI]                (1 card)
 */
export function moedasCompativeisInterseccao(
  moedaA: Moeda | string | null | undefined,
  moedaB: Moeda | string | null | undefined,
): Moeda[] {
  const listaA = moedasCompativeis(moedaA);
  const listaB = moedasCompativeis(moedaB);

  // Degenera para o eixo valido quando o outro nao tem matriz conhecida.
  if (listaA.length === 0) return listaB;
  if (listaB.length === 0) return listaA;

  const setB = new Set(listaB);
  return listaA.filter(m => setB.has(m));
}

/**
 * Identificadores dos campos de cotacao disponiveis no DTO CotacaoDTO.
 * Espelha os pares de conversao salvos (USD↔PYG, BRL↔PYG, USD↔BRL, ARS↔PYG).
 */
export type CampoCotacao = 'dolarGuarani' | 'realGuarani' | 'dolarReal' | 'pesoGuarani';

/**
 * Mapa de campos de cotacao visiveis por moeda padrao da empresa.
 *
 * Regra consolidada (abril/2026, revisao tech lead):
 *
 * - GUARANI: trio tradicional + ARS↔PYG (4 pares — universal). EXIBE pesoGuarani.
 * - REAL:    trio tradicional (USD↔PYG, BRL↔PYG, USD↔BRL). NAO exibe ARS.
 * - DOLAR:   trio tradicional (USD↔PYG, BRL↔PYG, USD↔BRL). NAO exibe ARS.
 * - PESO:    apenas ARS↔PYG (pesoGuarani).
 *
 * Importante: REAL e DOLAR nao exibem o campo ARS, mas o payload enviado ao
 * backend SEMPRE inclui pesoGuarani (com fallback = 1) — a persistencia
 * preventiva e resolvida em salvarCotacao(), nao neste mapa de visibilidade.
 *
 * Da mesma forma, PESO so exibe ARS, mas o payload sempre inclui o trio
 * tradicional (com fallback = 1).
 */
const CAMPOS_POR_MOEDA_PADRAO: Record<Moeda, CampoCotacao[]> = {
  [Moeda.GUARANI]: ['dolarGuarani', 'realGuarani', 'dolarReal', 'pesoGuarani'],
  [Moeda.REAL]:    ['dolarGuarani', 'realGuarani', 'dolarReal'],
  [Moeda.DOLAR]:   ['dolarGuarani', 'realGuarani', 'dolarReal'],
  [Moeda.PESO]:    ['pesoGuarani'],
};

/**
 * Retorna quais campos de cotacao devem ser exibidos/validados para a
 * empresa cuja moeda padrao e a informada. Se a moeda padrao nao estiver
 * definida (cadastro incompleto), faz fallback mostrando o trio tradicional
 * (comportamento historico: 3 pares USD/BRL/PYG).
 */
export function camposCotacaoVisiveis(
  moedaPadrao: Moeda | string | null | undefined,
): CampoCotacao[] {
  if (!moedaPadrao) {
    return ['dolarGuarani', 'realGuarani', 'dolarReal'];
  }
  const key = moedaPadrao as Moeda;
  const campos = CAMPOS_POR_MOEDA_PADRAO[key];
  return campos ? [...campos] : [];
}

/**
 * Converte o enum interno de moeda (GUARANI/REAL/DOLAR/PESO) para o codigo ISO
 * (PYG/BRL/USD/ARS) usado por `converterDireto`.
 *
 * Reusa o mapeamento ja centralizado em `MOEDA_CONFIG.symbol`, evitando uma
 * tabela enum->ISO duplicada. Retorna `null` para moeda nula/desconhecida —
 * permite ao chamador aplicar fallback defensivo.
 */
export function enumMoedaParaIso(
  enumMoeda: Moeda | string | null | undefined,
): 'PYG' | 'USD' | 'BRL' | 'ARS' | null {
  if (!enumMoeda) return null;
  const config = MOEDA_CONFIG[enumMoeda as Moeda];
  return config ? (config.symbol as 'PYG' | 'USD' | 'BRL' | 'ARS') : null;
}
